import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Users, Calendar, CheckCircle, AlertCircle, Home, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useGetUserApartmentQuery, useCreateBookingMutation, useGetUserMembershipStatusQuery } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import toast from 'react-hot-toast';
import Navbar from '../../components/common/Navbar';

const ApartmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useSelector((state: RootState) => state.auth);
  const isAuthenticated = !!user;
  const apartmentId = id ? parseInt(id) : null;
  const isValidId = apartmentId && !isNaN(apartmentId) && apartmentId > 0;

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [guests, setGuests] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingReference, setBookingReference] = useState('');

  const { data: apartmentResponse, isLoading } = useGetUserApartmentQuery(apartmentId!, {
    skip: !isValidId,
  });
  const { data: membershipStatusResponse } = useGetUserMembershipStatusQuery(undefined, {
    skip: !user,
  });
  const [createBooking] = useCreateBookingMutation();

  const apartment = apartmentResponse?.data?.apartment;
  const availability = apartmentResponse?.data?.availability || [];
  const membershipStatus = membershipStatusResponse?.data;
  const hasActiveMembership = membershipStatus?.is_active || false;
  const maxBookings = membershipStatus?.membership?.plan?.max_bookings_per_month || 0;

  const images = apartment?.images || [];
  let amenities: string[] = [];
  if (apartment?.amenities) {
    if (typeof apartment.amenities === 'string') {
      try {
        amenities = JSON.parse(apartment.amenities);
      } catch {
        amenities = apartment.amenities.split(',').map((a: string) => a.trim());
      }
    } else {
      amenities = Array.isArray(apartment.amenities) ? apartment.amenities : [];
    }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      if (window.innerWidth < 1024) {
        window.location.href = 'https://app.diasporawise.com';
      } else {
        navigate('/');
      }
      return;
    }

    if (!membershipStatus?.is_active) {
      toast.error('You need an active membership to book apartments');
      navigate('/membership/plans');
    }
  }, [user, membershipStatus, navigate]);

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkInDate || !checkOutDate) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    if (new Date(checkOutDate) <= new Date(checkInDate)) {
      toast.error('Check-out date must be after check-in date');
      return;
    }

    if (!hasActiveMembership) {
      toast.error('You need an active membership to book apartments');
      navigate('/membership/plans');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createBooking({
        apartment_id: apartmentId!,
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
        guests: guests,
      }).unwrap();

      if (result?.success) {
        setBookingReference(result.data.booking_reference);
        setShowBookingModal(false);
        setShowSuccessModal(true);
      } else {
        toast.error(result?.message || 'Failed to create booking');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to create booking. Please try again.');
      console.error('Booking error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.toISOString().split('T')[0];
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!apartment) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <Card className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Apartment Not Found</h2>
              <Button onClick={() => navigate('/apartments')}>Back to Apartments</Button>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/apartments')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Apartments</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card className="p-0 overflow-hidden bg-white">
              {images.length > 0 ? (
                <>
                  <div className="relative w-full h-96 bg-white">
                    <img
                      src={images[selectedImageIndex]}
                      alt={apartment.name}
                      className="w-full h-full object-cover cursor-pointer bg-white"
                      onClick={() => setShowImageModal(true)}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x600/FFFFFF/CCCCCC?text=No+Image';
                      }}
                    />
                    {images.length > 1 && (
                      <>
                        <button
                          onClick={() => setSelectedImageIndex((selectedImageIndex - 1 + images.length) % images.length)}
                          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={() => setSelectedImageIndex((selectedImageIndex + 1) % images.length)}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                      </>
                    )}
                  </div>
                  {images.length > 1 && (
                    <div className="p-4 border-t border-gray-200 bg-white">
                      <div className="flex gap-2 overflow-x-auto">
                        {images.map((img: string, idx: number) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedImageIndex(idx)}
                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all bg-white ${
                              selectedImageIndex === idx ? 'border-gray-800' : 'border-gray-200'
                            }`}
                          >
                            <img
                              src={img}
                              alt={`${apartment.name} ${idx + 1}`}
                              className="w-full h-full object-cover bg-white"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150/FFFFFF/CCCCCC?text=No+Image';
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-96 flex items-center justify-center bg-white">
                  <Home className="w-16 h-16 text-gray-400" />
                </div>
              )}
            </Card>

            {/* Details */}
            <Card className="p-6 bg-white">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{apartment.name}</h1>
                  {apartment.location && (
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{apartment.location}</span>
                    </div>
                  )}
                </div>
                {apartment.is_available ? (
                  <Badge variant="success" className="bg-green-500 text-white">
                    Available
                  </Badge>
                ) : (
                  <Badge variant="danger" className="bg-red-500 text-white">
                    Unavailable
                  </Badge>
                )}
              </div>

              {apartment.description && (
                <div className="border-t border-gray-200 pt-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
                  <p className="text-gray-700 whitespace-pre-line">{apartment.description}</p>
                </div>
              )}

              {/* Specifications */}
              <div className="border-t border-gray-200 pt-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Specifications</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Capacity</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Users className="w-4 h-4 text-gray-600" />
                      <p className="text-gray-900">{apartment.capacity} guests</p>
                    </div>
                  </div>
                  {apartment.address && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Address</label>
                      <p className="text-gray-900 mt-1">{apartment.address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Amenities */}
              {amenities.length > 0 && (
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Amenities</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {amenities.map((amenity: string, idx: number) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-gray-700 capitalize">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Booking Card */}
            <Card className="p-6 bg-white">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Book This Apartment</h3>
              
              {!hasActiveMembership && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-yellow-900 mb-1">Membership Required</h4>
                      <p className="text-sm text-yellow-700">
                        You need an active membership to book apartments.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Button
                onClick={() => setShowBookingModal(true)}
                disabled={!apartment.is_available || !hasActiveMembership}
                className="w-full bg-gray-800 hover:bg-gray-900 flex items-center justify-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                {apartment.is_available ? 'Select Dates & Book' : 'Not Available'}
              </Button>

              {membershipStatus?.membership && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-600">
                    Max bookings per month: {maxBookings}
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && images.length > 0 && (
        <Modal isOpen={showImageModal} onClose={() => setShowImageModal(false)} title="" size="xl">
          <div className="relative">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={images[selectedImageIndex]}
              alt={apartment.name}
              className="w-full h-auto max-h-[80vh] object-contain bg-white"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x600/FFFFFF/CCCCCC?text=No+Image';
              }}
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedImageIndex((selectedImageIndex - 1 + images.length) % images.length)}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => setSelectedImageIndex((selectedImageIndex + 1) % images.length)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-lg"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Booking Modal */}
      <Modal isOpen={showBookingModal} onClose={() => setShowBookingModal(false)} title="Book Apartment">
        <form onSubmit={handleBookingSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check-in Date *
            </label>
            <input
              type="date"
              value={checkInDate}
              onChange={(e) => setCheckInDate(e.target.value)}
              min={getMinDate()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check-out Date *
            </label>
            <input
              type="date"
              value={checkOutDate}
              onChange={(e) => setCheckOutDate(e.target.value)}
              min={checkInDate || getMinDate()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Guests *
            </label>
            <input
              type="number"
              value={guests}
              onChange={(e) => setGuests(Math.max(1, Math.min(apartment.capacity, parseInt(e.target.value) || 1)))}
              min={1}
              max={apartment.capacity}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Maximum capacity: {apartment.capacity} guests</p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowBookingModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !checkInDate || !checkOutDate}
              className="flex-1 bg-gray-800 hover:bg-gray-900"
            >
              {isSubmitting ? 'Booking...' : 'Confirm Booking'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/bookings');
        }}
        title="Booking Confirmed!"
      >
        <div className="text-center py-4">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <p className="text-gray-700 mb-2">
            Your booking has been confirmed successfully!
          </p>
          {bookingReference && (
            <p className="text-sm text-gray-600 mb-6">
              Booking Reference: <strong>{bookingReference}</strong>
            </p>
          )}
          <Button
            onClick={() => {
              setShowSuccessModal(false);
              navigate('/bookings');
            }}
            className="bg-gray-800 hover:bg-gray-900"
          >
            View My Bookings
          </Button>
        </div>
      </Modal>
      </div>
    </>
  );
};

export default ApartmentDetail;
